import type { AppContext } from '@application/context/AppContext';
import type { LicenceRuntime } from '@infra/licence/LicenceRuntime';
import type { Tier } from '@shared/types/Tenant';
import { getTenantProfile, saveTenantProfile } from '@application/persistence/TenantProfileService';
import { FeatureGate } from '@infra/licence/FeatureGate';
import { MODULE_DEFINITIONS } from '@application/licence/ModuleCatalog';
import { AuthError } from '@infra/auth/AuthService';

export const LICENCE_UPDATED_EVENT = 'nexxus:licence-updated';

function canonicalModules(features: string[] | undefined): string[] {
  const gate = FeatureGate.fromList(features);
  return MODULE_DEFINITIONS.filter((module) => gate.has(module.code)).map((module) => module.code);
}

/** Préserve les choix existants et active automatiquement tout nouveau module acheté. */
export function resolveEnabledModules(
  entitledModules: string[],
  previousLicensedModules?: string[],
  previousEnabledModules?: string[],
): string[] {
  if (previousEnabledModules === undefined) return [...entitledModules];

  const previousLicensed = new Set(canonicalModules(previousLicensedModules));
  const previousEnabled = new Set(canonicalModules(previousEnabledModules));
  return entitledModules.filter(
    (module) => previousEnabled.has(module) || !previousLicensed.has(module),
  );
}

/** Persiste les modules signés du certificat sur le tenant local. */
export async function syncLicenceEntitlements(
  ctx: AppContext,
  runtime: LicenceRuntime,
): Promise<void> {
  if (runtime.isReadOnly()) return;
  const claims = runtime.getState().claims;
  if (!claims?.tenant_id || !claims.features?.length) return;

  const tenant = await getTenantProfile(ctx, claims.tenant_id);
  if (!tenant) return;

  const modules = runtime.getEntitledModuleCodes().sort();
  const tierChanged = tenant.parametres?.licence_tier !== claims.tier;
  const enabledModules = tierChanged
    ? [...modules]
    : resolveEnabledModules(
        modules,
        tenant.parametres?.licensed_modules,
        tenant.parametres?.enabled_modules,
      );
  const roleProfiles = claims.role_views ?? {};
  try {
    await saveTenantProfile(ctx, {
      ...tenant,
      tier: claims.tier as Tier,
      parametres: {
        ...tenant.parametres,
        licensed_modules: modules,
        enabled_modules: enabledModules,
        role_profiles: roleProfiles,
        licence_synced_at: new Date().toISOString(),
        licence_tier: claims.tier,
      },
    });
  } catch (err) {
    // Seul un profil autorisé à écrire la fiche entreprise persiste la synchro (admin, RH). Un autre rôle
    // (lecture, comptable, salarié) ouvre l'application normalement : la sélection reste appliquée en mémoire.
    if (!(err instanceof AuthError && err.code === 'INSUFFICIENT_ROLE')) throw err;
  }
  runtime.applyModuleSelection(enabledModules);
}

export function notifyLicenceUpdated(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(LICENCE_UPDATED_EVENT));
}

/** Réapplique profils et préférences d’activation depuis le cache tenant (JWT = source de vérité). */
export async function applyCachedEntitlementsFromTenant(
  ctx: AppContext,
  runtime: LicenceRuntime,
): Promise<boolean> {
  const claims = runtime.getState().claims;
  if (!claims?.tenant_id || !claims.features?.length) return false;

  const tenant = await getTenantProfile(ctx, claims.tenant_id);
  if (!tenant) return false;

  const entitledFromJwt = canonicalModules(claims.features);
  const roleProfiles = tenant.parametres?.role_profiles ?? claims.role_views ?? {};
  runtime.applyEntitlements(
    entitledFromJwt,
    roleProfiles,
    tenant.parametres?.licence_tier ?? claims.tier,
  );

  const enabledModules = resolveEnabledModules(
    entitledFromJwt,
    tenant.parametres?.licensed_modules,
    tenant.parametres?.enabled_modules,
  );
  runtime.applyModuleSelection(enabledModules);
  return true;
}

/** Enregistre la sélection effective dans le stockage adapté au mode local ou distant. */
export async function saveEnabledModules(
  ctx: AppContext,
  runtime: LicenceRuntime,
  requestedModules: string[],
): Promise<string[]> {
  const claims = runtime.getState().claims;
  if (!claims?.tenant_id) throw new Error('Licence active introuvable');

  const tenant = await getTenantProfile(ctx, claims.tenant_id);
  if (!tenant) throw new Error('Entreprise active introuvable');

  const entitledModules = runtime.getEntitledModuleCodes().sort();
  const requested = new Set(canonicalModules(requestedModules));
  const enabledModules = entitledModules.filter((module) => requested.has(module));

  await saveTenantProfile(ctx, {
    ...tenant,
    parametres: {
      ...tenant.parametres,
      licensed_modules: entitledModules,
      enabled_modules: enabledModules,
    },
  });
  runtime.applyModuleSelection(enabledModules);
  notifyLicenceUpdated();
  return enabledModules;
}
